import { z } from "zod";
import { uuidSchema } from "./validation-common.js";
// ============================================
// ENUMS
// ============================================
export var NotificationType;
(function (NotificationType) {
    NotificationType["SHIFT_PUBLISHED"] = "SHIFT_PUBLISHED";
    NotificationType["SHIFT_UPDATED"] = "SHIFT_UPDATED";
    NotificationType["SHIFT_CANCELLED"] = "SHIFT_CANCELLED";
    NotificationType["SHIFT_REMINDER"] = "SHIFT_REMINDER";
    NotificationType["TIME_OFF_APPROVED"] = "TIME_OFF_APPROVED";
    NotificationType["TIME_OFF_REJECTED"] = "TIME_OFF_REJECTED";
    NotificationType["APPROVAL_PENDING"] = "APPROVAL_PENDING";
    NotificationType["CHAT_MESSAGE"] = "CHAT_MESSAGE";
    NotificationType["SCHEDULE_CHANGE"] = "SCHEDULE_CHANGE";
    NotificationType["SYSTEM_ALERT"] = "SYSTEM_ALERT";
    NotificationType["DOCUMENT_EXPIRY"] = "DOCUMENT_EXPIRY";
    NotificationType["REPORT_READY"] = "REPORT_READY";
    NotificationType["SWAP_REQUESTED"] = "SWAP_REQUESTED";
    NotificationType["SWAP_ACCEPTED"] = "SWAP_ACCEPTED";
    NotificationType["SWAP_APPROVED"] = "SWAP_APPROVED";
    NotificationType["SWAP_REJECTED"] = "SWAP_REJECTED";
    NotificationType["SWAP_PENDING_MANAGER_APPROVAL"] = "SWAP_PENDING_MANAGER_APPROVAL";
    NotificationType["TIMECARD_APPROVED"] = "TIMECARD_APPROVED";
    NotificationType["TIMECARD_CHANGE_REJECTED"] = "TIMECARD_CHANGE_REJECTED";
    NotificationType["TIMECARD_RETURNED"] = "TIMECARD_RETURNED";
    NotificationType["WELCOME"] = "WELCOME";
    NotificationType["OPEN_SHIFT_APPROVED"] = "OPEN_SHIFT_APPROVED";
    NotificationType["OPEN_SHIFT_REJECTED"] = "OPEN_SHIFT_REJECTED";
    NotificationType["LATE_CLOCK_IN"] = "LATE_CLOCK_IN";
    NotificationType["SIGNUP_COMPLETE"] = "SIGNUP_COMPLETE";
    NotificationType["INVITATION_REMINDER"] = "INVITATION_REMINDER";
    NotificationType["TIMECARD_SUBMIT_REQUESTED"] = "TIMECARD_SUBMIT_REQUESTED";
})(NotificationType || (NotificationType = {}));
export var DeviceType;
(function (DeviceType) {
    DeviceType["WEB"] = "WEB";
    DeviceType["IOS"] = "IOS";
    DeviceType["ANDROID"] = "ANDROID";
})(DeviceType || (DeviceType = {}));
export var NotificationChannel;
(function (NotificationChannel) {
    NotificationChannel["PUSH"] = "push";
    NotificationChannel["IN_APP"] = "in_app";
    NotificationChannel["EMAIL"] = "email";
})(NotificationChannel || (NotificationChannel = {}));
// ============================================
// INPUT SCHEMAS
// ============================================
export const CreateNotificationInputSchema = z.object({
    userId: uuidSchema,
    type: z.nativeEnum(NotificationType),
    title: z.string().trim().min(1).max(255),
    body: z.string().trim().min(1).max(5000),
    data: z.record(z.string(), z.unknown()).optional(), // Additional payload (e.g., shiftId, conversationId)
    expiresAt: z.coerce.date().optional(),
});
// Bulk create notifications (e.g., for shift publish to multiple users)
export const CreateBulkNotificationsInputSchema = z.object({
    userIds: z.array(uuidSchema).min(1).max(1000),
    type: z.nativeEnum(NotificationType),
    title: z.string().trim().min(1).max(255),
    body: z.string().trim().min(1).max(5000),
    data: z.record(z.string(), z.unknown()).optional(),
    expiresAt: z.coerce.date().optional(),
});
export const UpdateNotificationPreferenceInputSchema = z.object({
    type: z.nativeEnum(NotificationType),
    enabled: z.boolean(),
    channels: z.array(z.nativeEnum(NotificationChannel)).min(1).optional(),
});
export const RegisterDeviceInputSchema = z.object({
    deviceType: z.nativeEnum(DeviceType),
    deviceToken: z.string().optional(), // Push token for mobile (APNs/FCM)
    deviceInfo: z.record(z.string(), z.unknown()).optional(), // Browser/device metadata
});
export const GetNotificationsQuerySchema = z.object({
    limit: z.coerce.number().min(1).max(100).default(50),
    cursor: uuidSchema.optional(),
    unreadOnly: z.coerce.boolean().optional(),
    type: z.nativeEnum(NotificationType).optional(),
});
export const MarkNotificationsReadInputSchema = z.object({
    notificationIds: z.array(uuidSchema).min(1).max(100).optional(), // If not provided, marks all as read
});
// ============================================
// RESPONSE TYPES
// ============================================
// ============================================
// RESPONSE SCHEMAS
// ============================================
export const NotificationResponseSchema = z.object({
    id: z.uuid(),
    type: z.nativeEnum(NotificationType),
    title: z.string(),
    body: z.string(),
    data: z.record(z.string(), z.unknown()).nullish(),
    isRead: z.boolean(),
    readAt: z.iso.datetime().nullish(),
    createdAt: z.iso.datetime(),
    expiresAt: z.iso.datetime().nullish(),
});
export const UserSessionResponseSchema = z.object({
    id: z.uuid(),
    deviceType: z.nativeEnum(DeviceType),
    deviceInfo: z.record(z.string(), z.unknown()).nullish(),
    isActive: z.boolean(),
    lastActiveAt: z.iso.datetime(),
    createdAt: z.iso.datetime(),
});
